/**
 * ACESSO SOMENTE VISUALIZAÇÃO (definido no cadastro do Portal).
 *
 * O Portal responde, na checagem de acesso (`/api/acesso`), se a pessoa é
 * "somente visualização" e quais abas deste sistema ela pode ver. Aqui fica a
 * parte da TELA:
 *   - `podeVerAba` / `primeiraAbaLiberada`: o menu mostra só as abas liberadas;
 *   - `ativarModoLeitura`: faixa avisando o modo, esconde os botões de gravar
 *     (Novo, Salvar, Editar, Excluir, Importar, Aprovar…) e recusa, já no
 *     navegador, qualquer gravação na API.
 * Quem garante de verdade é o servidor (server/modo-leitura.ts), que recusa a
 * gravação mesmo que alguém contorne a tela.
 *
 * Arquivo igual em todos os sistemas: mudou aqui, copie para os outros.
 */

export interface Visualizacao {
  somenteLeitura: boolean;
  /** Abas liberadas neste sistema; null = todas. */
  abas: string[] | null;
}

export const ACESSO_COMPLETO: Visualizacao = { somenteLeitura: false, abas: null };

/** Lê os campos novos da resposta do Portal (sistemas antigos do Portal não mandam = acesso completo). */
export function visualizacaoDaResposta(r: unknown): Visualizacao {
  const o = (r ?? {}) as { somenteLeitura?: unknown; abas?: unknown };
  const somenteLeitura = o.somenteLeitura === true;
  const abas = somenteLeitura && Array.isArray(o.abas) ? o.abas.map(String) : null;
  return { somenteLeitura, abas };
}

export function podeVerAba(v: Visualizacao, aba: string): boolean {
  return !v.abas || v.abas.includes(aba);
}

/** A primeira aba que a pessoa pode ver, na ordem do menu. */
export function primeiraAbaLiberada(v: Visualizacao, ordem: string[]): string | null {
  return ordem.find((k) => podeVerAba(v, k)) ?? null;
}

/* ---------------------------------------------------------------- tela --- */

const MENSAGEM = "Seu acesso é somente para visualização.";

/**
 * Texto (ou title/aria-label) de controle que GRAVA algo. "Baixar", "Atualizar"
 * (recarregar), "Limpar filtros", "Voltar", "Fechar", "Cancelar", "Ver" ficam.
 * "Gerar" sozinho também fica ("Gerar PDF" só lê); só as gerações que gravam.
 */
const GRAVA =
  /^(?:[+➕]|(?:novo|nova|novos|novas|adicionar|cadastrar|incluir|criar|salvar|gravar|editar|alterar|excluir|remover|apagar|deletar|lixeira|importar|enviar|anexar|carregar arquivo|substituir|upload|aprovar|reprovar|recusar|aceitar|registrar|lançar|lancar|finalizar|concluir|confirmar|conferir|emitir|sincronizar|duplicar|arquivar|restaurar|desativar|ativar|inativar|reativar|atribuir|vincular|desvincular|desfazer|agendar|reagendar|programar|solicitar|pedir|dar baixa|devolver|retirar|movimentar|atender|assinar|bater ponto|marcar como|marcar benef|responder|avaliar|mover|transferir|redefinir|resetar|trocar|efetivar|revogar|reabrir|encerrar|montar|parabenizar|disparar|preencher|sugerir|fechar o m[eê]s|despachar|liberar|trazer da norma|conferi\b|tirar da lista|voltar para o estoque|melhorar com ia|fechar confer|cancelar (?:nota|requisi|pedido|agendamento|solicita)|gerar (?:link|um link|links|requisi|com ia|as datas|o plano|plano|diagn|pelas regras))\b)/i;

/** Controles que só LEEM, mesmo começando por um verbo da lista acima. */
const SO_LE = /baixar|download|exportar|planilha|imprimir|visualizar/i;

/** Onde NUNCA esconder: navegação, barra do topo e o próprio aviso. */
const FORA = ".sidebar, .topbar, header.topbar, nav, .modo-leitura-faixa";

function textoDoControle(el: Element): string {
  const t = (el.textContent ?? "").replace(/\s+/g, " ").trim();
  const txt = t || el.getAttribute("title") || el.getAttribute("aria-label") || "";
  // Ícone/emoji na frente ("✨ Melhorar com IA") não pode esconder o verbo.
  return txt.replace(/^[^\p{L}\p{N}+➕]+/u, "");
}

function esconderControles(raiz: ParentNode) {
  raiz
    .querySelectorAll<HTMLElement>(
      'button, [role="button"], a.btn, a.button, input[type="submit"], input[type="button"], input[type="file"], label.btn, [data-grava]',
    )
    .forEach((el) => {
      if (el.dataset.modoLeitura === "oculto") return;
      // Marcação explícita no código: <button data-grava> some sempre.
      const marcado = el.hasAttribute("data-grava");
      if (!marcado && el.closest(FORA)) return;
      const txt = el instanceof HTMLInputElement && el.type !== "file" ? el.value : textoDoControle(el);
      const deArquivo = el instanceof HTMLInputElement && el.type === "file";
      if (marcado || deArquivo || (GRAVA.test(txt) && !SO_LE.test(txt))) {
        el.dataset.modoLeitura = "oculto";
        el.style.setProperty("display", "none", "important");
      }
    });
}

let desfazer: (() => void) | null = null;

/**
 * Liga/desliga o modo somente visualização na página inteira. Idempotente:
 * chamar de novo com o mesmo valor não duplica nada.
 */
export function ativarModoLeitura(ativo: boolean): void {
  if (!ativo) {
    desfazer?.();
    desfazer = null;
    return;
  }
  if (desfazer) return;

  // 1) Faixa fixa no topo (discreta — vermelho é só para erro).
  const faixa = document.createElement("div");
  faixa.className = "modo-leitura-faixa";
  faixa.setAttribute("role", "status");
  faixa.textContent = "Modo somente visualização — você pode consultar, mas não criar, editar ou excluir.";
  Object.assign(faixa.style, {
    position: "fixed",
    left: "50%",
    bottom: "12px",
    transform: "translateX(-50%)",
    zIndex: "2147483000",
    padding: "6px 14px",
    borderRadius: "999px",
    font: "500 12.5px/1.3 Calibri, 'Segoe UI', sans-serif",
    background: "rgba(40, 52, 46, 0.92)",
    color: "#fff",
    boxShadow: "0 2px 10px rgba(0,0,0,.18)",
    pointerEvents: "none",
    maxWidth: "calc(100vw - 32px)",
    textAlign: "center",
  } as Partial<CSSStyleDeclaration>);
  document.body.appendChild(faixa);
  document.body.classList.add("modo-leitura");

  // 2) Esconde os botões de gravar agora e a cada mudança da tela.
  esconderControles(document);
  let agendado = false;
  const obs = new MutationObserver(() => {
    if (agendado) return;
    agendado = true;
    requestAnimationFrame(() => {
      agendado = false;
      esconderControles(document);
    });
  });
  obs.observe(document.body, { childList: true, subtree: true });

  // 3) Nenhuma gravação sai do navegador (o servidor recusa de qualquer jeito).
  const fetchOriginal = window.fetch;
  const fetchSemGravar = (input: RequestInfo | URL, init?: RequestInit) => {
    const metodo = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const grava = !["GET", "HEAD", "OPTIONS"].includes(metodo);
    const ehLogin = /\/(login|logout|sso)(\/|\?|$)/.test(url);
    if (grava && !ehLogin && /\/api\//.test(url)) {
      return Promise.resolve(
        new Response(JSON.stringify({ error: MENSAGEM, message: MENSAGEM, code: "SOMENTE_LEITURA" }), {
          status: 403,
          headers: { "content-type": "application/json" },
        }),
      );
    }
    return fetchOriginal.call(window, input, init);
  };
  window.fetch = Object.assign(fetchSemGravar, fetchOriginal) as typeof fetch;

  desfazer = () => {
    obs.disconnect();
    faixa.remove();
    document.body.classList.remove("modo-leitura");
    window.fetch = fetchOriginal;
    document.querySelectorAll<HTMLElement>('[data-modo-leitura="oculto"]').forEach((el) => {
      el.style.removeProperty("display");
      delete el.dataset.modoLeitura;
    });
  };
}
